import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Home, Building2, DoorOpen, Check } from 'lucide-react';
import Layout from '@/components/Layout';

export default function SetupPage() {
  const [step, setStep] = useState(1);
  const [pgData, setPgData] = useState({
    name: '',
    address: '',
    defaultRent: 5000,
    defaultDeposit: 10000,
    depositRefundablePercent: 100
  });
  const [floors, setFloors] = useState([{ floorNumber: 1, floorName: 'Ground Floor' }]);
  const [rooms, setRooms] = useState([]);
  const [createdPgId, setCreatedPgId] = useState(null);
  const [createdFloors, setCreatedFloors] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handlePgSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = createdPgId
        ? await api.updatePG(createdPgId, pgData)
        : await api.createPG(pgData);

      setCreatedPgId(response.data.id);
      toast.success(createdPgId ? 'PG updated successfully!' : 'PG created successfully!');
      setStep(2);
    } catch (error) {
      toast.error(createdPgId ? 'Failed to update PG' : 'Failed to create PG');
    } finally {
      setLoading(false);
    }
  };

  const handleFloorsSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const floorResponses = [];
      for (const floor of floors) {
        const payload = {
          pgId: createdPgId,
          floorNumber: floor.floorNumber,
          floorName: floor.floorName
        };
        const response = floor.id
          ? await api.updateFloor(floor.id, payload)
          : await api.createFloor(payload);
        floorResponses.push(response.data);
      }

      setFloors(floorResponses);
      setCreatedFloors(floorResponses);
      toast.success(createdFloors.length > 0 ? 'Floors updated successfully!' : 'Floors added successfully!');
      setStep(3);
    } catch (error) {
      toast.error(createdFloors.length > 0 ? 'Failed to update floors' : 'Failed to add floors');
    } finally {
      setLoading(false);
    }
  };

  const handleRoomsSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      for (const room of rooms) {
        await api.createRoom(room);
      }
      toast.success('Rooms and beds created successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to add rooms');
    } finally {
      setLoading(false);
    }
  };

  const addFloor = () => {
    setFloors([...floors, { floorNumber: floors.length + 1, floorName: `Floor ${floors.length + 1}` }]);
  };

  const addRoom = () => {
    if (createdFloors.length === 0) return;
    setRooms([...rooms, {
      floorId: createdFloors[0].id,
      roomNumber: (rooms.length + 1).toString(),
      roomType: 'Shared',
      numBeds: 2,
      rentPerBed: pgData.defaultRent
    }]);
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 font-heading mb-2">Setup Your PG</h1>
          <p className="text-slate-600">Let's get your property ready in 3 simple steps</p>
        </div>

        {/* Progress steps */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-2">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
              step >= 1 ? 'bg-primary text-white' : 'bg-slate-200 text-slate-500'
            }`}>
              {step > 1 ? <Check className="w-6 h-6" /> : '1'}
            </div>
            <span className="text-sm font-medium text-slate-700">PG Details</span>
          </div>
          <div className="flex-1 h-1 bg-slate-200 mx-2">
            <div className={`h-full bg-primary transition-all ${step >= 2 ? 'w-full' : 'w-0'}`} />
          </div>
          <div className="flex items-center gap-2">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
              step >= 2 ? 'bg-primary text-white' : 'bg-slate-200 text-slate-500'
            }`}>
              {step > 2 ? <Check className="w-6 h-6" /> : '2'}
            </div>
            <span className="text-sm font-medium text-slate-700">Add Floors</span>
          </div>
          <div className="flex-1 h-1 bg-slate-200 mx-2">
            <div className={`h-full bg-primary transition-all ${step >= 3 ? 'w-full' : 'w-0'}`} />
          </div>
          <div className="flex items-center gap-2">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
              step >= 3 ? 'bg-primary text-white' : 'bg-slate-200 text-slate-500'
            }`}>
              3
            </div>
            <span className="text-sm font-medium text-slate-700">Add Rooms</span>
          </div>
        </div>

        {/* Step 1: PG Details */}
        {step === 1 && (
          <form onSubmit={handlePgSubmit} className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Home className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 font-heading">PG Details</h2>
            </div>

            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">PG Name</Label>
              <Input
                placeholder="e.g., Sharma PG, Royal Paying Guest"
                value={pgData.name}
                onChange={(e) => setPgData({ ...pgData, name: e.target.value })}
                className="h-12 rounded-lg"
                required
                data-testid="pg-name-input"
              />
            </div>

            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Address</Label>
              <Input
                placeholder="Full address of your PG"
                value={pgData.address}
                onChange={(e) => setPgData({ ...pgData, address: e.target.value })}
                className="h-12 rounded-lg"
                required
                data-testid="pg-address-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Default Rent per Bed (₹)</Label>
                <Input
                  type="number"
                  placeholder="5000"
                  value={pgData.defaultRent}
                  onChange={(e) => setPgData({ ...pgData, defaultRent: parseFloat(e.target.value) })}
                  className="h-12 rounded-lg"
                  required
                  data-testid="default-rent-input"
                />
              </div>
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Default Deposit (₹)</Label>
                <Input
                  type="number"
                  placeholder="10000"
                  value={pgData.defaultDeposit}
                  onChange={(e) => setPgData({ ...pgData, defaultDeposit: parseFloat(e.target.value) })}
                  className="h-12 rounded-lg"
                  required
                  data-testid="default-deposit-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-12 rounded-xl bg-primary hover:bg-primary/90 text-white font-semibold"
              disabled={loading}
              data-testid="pg-next-button"
            >
              {loading ? 'Creating...' : 'Next: Add Floors'}
            </Button>
          </form>
        )}

        {/* Step 2: Add Floors */}
        {step === 2 && (
          <form onSubmit={handleFloorsSubmit} className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Building2 className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 font-heading">Add Floors</h2>
            </div>

            {floors.map((floor, index) => (
              <div key={index} className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Floor Number</Label>
                  <Input
                    type="number"
                    value={floor.floorNumber}
                    onChange={(e) => {
                      const newFloors = [...floors];
                      newFloors[index].floorNumber = parseInt(e.target.value);
                      setFloors(newFloors);
                    }}
                    className="h-11 rounded-lg"
                    required
                    data-testid={`floor-number-${index}`}
                  />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Floor Name</Label>
                  <Input
                    value={floor.floorName}
                    onChange={(e) => {
                      const newFloors = [...floors];
                      newFloors[index].floorName = e.target.value;
                      setFloors(newFloors);
                    }}
                    className="h-11 rounded-lg"
                    required
                    data-testid={`floor-name-${index}`}
                  />
                </div>
              </div>
            ))}

            <Button
              type="button"
              onClick={addFloor}
              variant="outline"
              className="w-full h-11 rounded-lg"
              data-testid="add-floor-button"
            >
              + Add Another Floor
            </Button>

            <div className="flex gap-3">
              <Button
                type="button"
                onClick={() => setStep(1)}
                variant="outline"
                className="flex-1 h-12 rounded-xl"
                data-testid="floor-back-button"
              >
                Back
              </Button>
              <Button
                type="submit"
                className="flex-1 h-12 rounded-xl bg-primary hover:bg-primary/90 text-white font-semibold"
                disabled={loading}
                data-testid="floor-next-button"
              >
                {loading ? 'Adding...' : 'Next: Add Rooms'}
              </Button>
            </div>
          </form>
        )}

        {/* Step 3: Add Rooms */}
        {step === 3 && (
          <form onSubmit={handleRoomsSubmit} className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <DoorOpen className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 font-heading">Add Rooms</h2>
            </div>

            {rooms.map((room, index) => (
              <div key={index} className="p-4 bg-slate-50 rounded-lg space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Floor</Label>
                    <select
                      value={room.floorId}
                      onChange={(e) => {
                        const newRooms = [...rooms];
                        newRooms[index].floorId = e.target.value;
                        setRooms(newRooms);
                      }}
                      className="h-11 w-full px-3 rounded-lg border border-slate-300 bg-white"
                      required
                      data-testid={`room-floor-${index}`}
                    >
                      {createdFloors.map((floor) => (
                        <option key={floor.id} value={floor.id}>{floor.floorName}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Room Number</Label>
                    <Input
                      value={room.roomNumber}
                      onChange={(e) => {
                        const newRooms = [...rooms];
                        newRooms[index].roomNumber = e.target.value;
                        setRooms(newRooms);
                      }}
                      className="h-11 rounded-lg"
                      required
                      data-testid={`room-number-${index}`}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Type</Label>
                    <select
                      value={room.roomType}
                      onChange={(e) => {
                        const newRooms = [...rooms];
                        newRooms[index].roomType = e.target.value;
                        setRooms(newRooms);
                      }}
                      className="h-11 w-full px-3 rounded-lg border border-slate-300 bg-white"
                      data-testid={`room-type-${index}`}
                    >
                      <option>Shared</option>
                      <option>Private</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Beds</Label>
                    <Input
                      type="number"
                      min="1"
                      value={room.numBeds}
                      onChange={(e) => {
                        const newRooms = [...rooms];
                        newRooms[index].numBeds = parseInt(e.target.value);
                        setRooms(newRooms);
                      }}
                      className="h-11 rounded-lg"
                      required
                      data-testid={`room-beds-${index}`}
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Rent (₹)</Label>
                    <Input
                      type="number"
                      value={room.rentPerBed}
                      onChange={(e) => {
                        const newRooms = [...rooms];
                        newRooms[index].rentPerBed = parseFloat(e.target.value);
                        setRooms(newRooms);
                      }}
                      className="h-11 rounded-lg"
                      required
                      data-testid={`room-rent-${index}`}
                    />
                  </div>
                </div>
              </div>
            ))}

            <Button
              type="button"
              onClick={addRoom}
              variant="outline"
              className="w-full h-11 rounded-lg"
              data-testid="add-room-button"
            >
              + Add Another Room
            </Button>

            <div className="flex gap-3">
              <Button
                type="button"
                onClick={() => setStep(2)}
                variant="outline"
                className="flex-1 h-12 rounded-xl"
                data-testid="room-back-button"
              >
                Back
              </Button>
              <Button
                type="submit"
                className="flex-1 h-12 rounded-xl bg-primary hover:bg-primary/90 text-white font-semibold"
                disabled={loading || rooms.length === 0}
                data-testid="room-finish-button"
              >
                {loading ? 'Creating...' : 'Finish Setup'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </Layout>
  );
}
